import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { comics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { Helmet } from '@/pages/home/components/SEO';

const categoryNames: Record<string, string> = {};
categories.forEach((c) => { categoryNames[c.slug] = c.name; });

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const filteredComics = activeCategory === 'all'
    ? comics
    : comics.filter((c) => c.category === activeCategory);

  const categoryName = categoryNames[activeCategory] || '全部';
  const pageTitle = activeCategory === 'all'
    ? '全部韩国漫画'
    : `${categoryName}韩国漫画`;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${pageTitle} - 韩漫网`,
    description: `韩漫网${pageTitle}分类，提供海量${categoryName}类韩国漫画在线观看，支持下拉式阅读，每日更新。`,
    url: `https://hanmanwang.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫网',
      url: 'https://hanmanwang.com',
    },
  };

  return (
    <>
      <Helmet
        title={`${pageTitle} - 韩漫网 | 下拉式韩国漫画在线观看`}
        description={`韩漫网${pageTitle}分类，提供海量${categoryName}类韩国漫画免费在线观看。支持下拉式阅读，每日更新，无需跳转，体验连贯流畅。`}
        keywords={`${pageTitle},韩漫网,韩漫大全,下拉式韩国漫画,韩漫在线观看,韩国漫画`}
        canonicalUrl={`https://hanmanwang.com/category/${activeCategory}`}
        schema={[schema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="py-4">
              <Breadcrumb items={[
                { label: '首页', href: '/' },
                { label: pageTitle },
              ]} />
            </div>

            <div className="flex flex-wrap items-center gap-2 pb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.name}
                </Link>
              ))}
            </div>

            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
              {pageTitle}
            </h1>
            <p className="text-sm text-foreground-500 mb-8">
              共 {filteredComics.length} 部作品
            </p>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5 pb-16">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} size="medium" />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <span className="w-8 h-8 flex items-center justify-center text-foreground-400">
                    <i className="ri-emotion-sad-line text-2xl"></i>
                  </span>
                </div>
                <p className="text-foreground-500">暂无相关内容</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}